<script>
import { customerMethods } from '@state/helpers'
import {
  required,
  sameAs,
  email,
  maxLength,
  minLength,
} from 'vuelidate/lib/validators'
import {
  passwordValidator,
  maxNameInputValidator,
  nameValidator,
} from '@utils/validators'

export default {
  props: {
    fromCheckout: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      emailUpdates: false,
      wasValidated: false,
      registrationError: null,
      tryingToRegister: false,
    }
  },
  validations: {
    firstName: {
      required,
      maxNameInputValidator,
      nameValidator,
      minLength: minLength(2),
    },
    lastName: {
      required,
      maxNameInputValidator,
      nameValidator,
      minLength: minLength(2),
    },
    email: {
      required,
      email,
      maxLength: maxLength(50),
    },
    password: {
      required,
      passwordValidator,
    },
    confirmPassword: {
      required,
      sameAsPassword: sameAs('password'),
      passwordValidator,
    },
  },
  methods: {
    ...customerMethods,
    // Try to log the user in with the username
    // and password they provided.
    async tryToRegister() {
      // Reset the authError if it existed.
      this.authError = null
      this.$v.$touch()
      if (this.$v.$invalid) {
        this.wasValidated = true
      } else {
        this.tryingToRegister = true

        return this.register({
          firstName: this.firstName,
          lastName: this.lastName,
          email: this.email,
          password: this.password,
          emailOptIn: this.emailUpdates,
        })
          .then((response) => {
            this.tryingToRegister = false
            if (this.$gtag) {
              this.$gtag.event('sign_up', {})
            }
            if (!this.fromCheckout) {
              this.$router.push({ name: 'dashboard' })
            }
          })
          .catch((error) => {
            this.tryingToRegister = false
            let errorMessage =
              error.response.status === 409
                ? 'An account already exists for this email address.'
                : error.response.data.message
            this.registrationError = errorMessage
          })
      }
    },
  },
}
</script>

<template>
  <form
    :class="{ 'was-validated ': wasValidated }"
    class="needs-validation"
    @submit.prevent="tryToRegister"
  >
    <b-alert v-if="registrationError" variant="danger" show dismissible>
      {{ registrationError }}
    </b-alert>
    <div class="row">
      <div class="col-md-6">
        <BaseInput
          v-model="$v.firstName.$model"
          name="firstName"
          label="First Name"
          :has-error="$v.firstName.$error && wasValidated"
        >
          <div v-if="!$v.firstName.required" class="invalid-feedback"
            >Field is required</div
          >
          <div
            v-if="!$v.firstName.maxNameInputValidator"
            class="invalid-feedback"
            >Please enter no more than 20 characters.</div
          >
          <div
            v-if="!$v.firstName.nameValidator && $v.firstName.required"
            class="invalid-feedback"
            >Only alphanumeric characters or dashes are allowed.</div
          >
          <div v-if="!$v.firstName.minLength" class="invalid-feedback"
            >Please enter at least 2 characters</div
          >
        </BaseInput>
      </div>
      <div class="col-md-6">
        <BaseInput
          v-model="$v.lastName.$model"
          name="lastName"
          label="Last Name"
          :has-error="$v.lastName.$error && wasValidated"
        >
          <div v-if="!$v.lastName.required" class="invalid-feedback"
            >Field is required</div
          >
          <div
            v-if="!$v.lastName.maxNameInputValidator"
            class="invalid-feedback"
            >Please enter no more than 20 characters.</div
          >
          <div
            v-if="!$v.lastName.nameValidator && $v.lastName.required"
            class="invalid-feedback"
            >Only alphanumeric characters or dashes are allowed.</div
          >
          <div v-if="!$v.lastName.minLength" class="invalid-feedback"
            >Please enter at least 2 characters</div
          >
        </BaseInput>
      </div>
    </div>
    <div class="row">
      <div class="col-md-12">
        <BaseInput
          v-model="$v.email.$model"
          name="email"
          label="E-mail"
          :has-error="$v.email.$error && wasValidated"
        >
          <div v-if="!$v.email.required" class="invalid-feedback"
            >Field is required</div
          >
          <div v-if="!$v.email.email" class="invalid-feedback"
            >Please enter a valid email address</div
          >
          <div v-if="!$v.email.maxLength" class="invalid-feedback"
            >Please enter no more than 50 characters</div
          >
        </BaseInput>
      </div>
    </div>
    <div class="row">
      <div class="col-md-6 col-12">
        <BaseInput
          v-model="$v.password.$model"
          name="password"
          type="password"
          label="Password"
          :has-error="$v.password.$error && wasValidated"
        >
          <div v-if="!$v.password.required" class="invalid-feedback"
            >Field is required</div
          >
          <div
            v-if="!$v.password.passwordValidator && $v.password.required"
            class="invalid-feedback"
            >Your password must follow the password creation rules. Please check
            your password and try again.</div
          >
        </BaseInput>
        <BaseInput
          v-model="$v.confirmPassword.$model"
          name="confirmPassword"
          type="password"
          label="Confirm Password"
          :has-error="$v.confirmPassword.$error && wasValidated"
        >
          <div v-if="!$v.confirmPassword.required" class="invalid-feedback"
            >Field is required</div
          >
          <div
            v-if="!$v.confirmPassword.sameAsPassword"
            class="invalid-feedback"
            >Please enter the same password again</div
          >
          <div
            v-if="
              !$v.confirmPassword.passwordValidator &&
                $v.confirmPassword.required
            "
            class="invalid-feedback"
            >Your password must follow the password creation rules. Please check
            your password and try again.</div
          >
        </BaseInput>
      </div>
      <div class="col-md-6 col-12">
        <div :class="$style.passwordRules">
          <p class="mb-0"
            ><strong>Your password must contain the following:</strong></p
          >
          <ul>
            <li>At least eight (8) characters</li>
            <li>A mix of CAPITAL [A-Z] and lower case [abc] letters, and</li>
            <li>At least one number [123] or special character (& ! @)</li>
          </ul>
        </div>
      </div>
    </div>
    <div class="row mb-3">
      <div class="col-md-12">
        <input id="emailUpdates" v-model="emailUpdates" type="checkbox" />
        Yes: Please send me email updates and exclusive offers from AutoCheck
      </div>
    </div>
    <BaseButton :disabled="tryingToRegister" type="submit">
      <BaseIcon v-if="tryingToRegister" name="sync" spin />
      <span v-else>
        {{ fromCheckout ? 'Create Account' : 'Sign in' }}
      </span>
    </BaseButton>
  </form>
</template>

<style lang="scss" module>
@import '@design';

.passwordRules {
  margin-top: 28px;
  font-size: 13px;
  ul {
    padding-left: 15px;
    font-style: italic;
  }
}
</style>
